import React, { useState } from "react";
import { toast } from "sonner";
import { Trash2, Plus } from "lucide-react";
import api, { formatError } from "../lib/api";
import { useApi } from "../hooks/useApi";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";

function AccountRow({ a, reload }) {
  const [name, setName] = useState(a.name);
  const save = async (patch) => {
    try { await api.put(`/admin/payment-accounts/${a.id}`, { name, description: a.description, active: a.active, counts_toward_balance: a.counts_toward_balance !== false, ...patch }); toast.success("Account saved"); reload(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const remove = async () => {
    try { await api.delete(`/admin/payment-accounts/${a.id}`); toast.success("Account deleted"); reload(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const counts = a.counts_toward_balance !== false;
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-lg border border-gold/30 bg-parchment-card p-2" data-testid={`account-row-${a.id}`}>
      <Input value={name} onChange={(e) => setName(e.target.value)} onBlur={() => name !== a.name && save({ name })} className="h-8 flex-1 min-w-[140px]" data-testid={`account-name-${a.id}`} />
      <label className="flex items-center gap-1.5 text-xs" title="Active (visible to members)"><Switch checked={a.active} onCheckedChange={(v) => save({ active: v })} data-testid={`account-active-${a.id}`} /> Active</label>
      <label className={`flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs ${counts ? "bg-palm/10 text-palm" : "bg-gold/15 text-crimson"}`} title="When on, payments to this account reduce the member's balance due. When off (e.g. Donation), they are recorded with thanks but never reduce arrears.">
        <Switch checked={counts} onCheckedChange={(v) => save({ counts_toward_balance: v })} data-testid={`account-counts-${a.id}`} /> {counts ? "Counts toward balance" : "Gift — not in balance"}
      </label>
      <Button size="icon" variant="ghost" onClick={remove} className="h-8 w-8 text-destructive" data-testid={`account-delete-${a.id}`}><Trash2 size={14} /></Button>
    </div>
  );
}

export function PaymentAccountsAdmin() {
  const { data: accounts, reload } = useApi("/admin/payment-accounts");
  const [form, setForm] = useState({ name: "", description: "" });
  const create = async () => {
    try { await api.post("/admin/payment-accounts", { ...form, active: true }); toast.success("Account created"); setForm({ name: "", description: "" }); reload(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  return (
    <div className="glass rounded-2xl p-6 space-y-3" data-testid="payment-accounts-admin">
      <h3 className="font-serif-display text-xl font-bold text-crimson">Payment accounts</h3>
      <p className="text-xs text-muted-foreground">Members choose one of these when paying (e.g. Registration, Dues). Income is broken down by account in Finance reports and the AGM pack. Inactive accounts stay in reports but are hidden from members. Only accounts marked <b>Counts toward balance</b> reduce a member's balance due — gifts such as Donations are reported separately on their statement. Changing this applies immediately to every member's balance.</p>
      <div className="space-y-2">{(accounts || []).map((a) => <AccountRow key={a.id} a={a} reload={reload} />)}</div>
      <div className="border-t border-gold/20 pt-3">
        <Label>New account</Label>
        <div className="mt-1 flex gap-2">
          <Input placeholder="Name (e.g. Building Fund)" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="account-new-name" />
          <Input placeholder="Description (optional)" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="account-new-desc" />
          <Button onClick={create} disabled={!form.name.trim()} className="bg-crimson" data-testid="account-create"><Plus size={15} /></Button>
        </div>
      </div>
    </div>
  );
}
